import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Container,
  Alert,
  Row,
  Col,
  Form,
  Pagination,
} from 'react-bootstrap';
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaSortUp,
  FaSortDown,
  FaQuestionCircle,
} from 'react-icons/fa';
import ConfirmDeletion from '../components/ConfirmDeletion';
import Message from '../components/Message';
import Loader from '../components/Loader';
import {
  useGetSquadDataQuery,
  useDeleteSquadDataMutation,
  useCreateSquadDataMutation,
  useUpdateSquadDataMutation,
} from '../slices/squadDataApiSlice';
import AddSquadDataModal from '../components/Squad/AddSquadDataModal';
import EditSquadDataModal from '../components/Squad/EditSquadDataModal';
import ScreenTabs from '../components/ScreenTabs';

import { toast } from 'react-toastify';

const MANAGE_DATA_TABS = [
  { label: 'Game Data', to: '/ShotDataScreen' },
  { label: 'Squad Data', to: '/SquadDataScreen' },
];

const SquadDataScreen = () => {
  // ←— add refetchOnMountOrArgChange here:
  const { data, isLoading, error } = useGetSquadDataQuery(
    undefined,
    { refetchOnMountOrArgChange: true }
  );

  const [deleteEntry] = useDeleteSquadDataMutation();
  const [createEntry] = useCreateSquadDataMutation();
  const [updateEntry] = useUpdateSquadDataMutation();

  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEditEntry, setSelectedEditEntry] = useState(null);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterPosition, setFilterPosition] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const pageTitleRef = useRef(null);
  const addPlayerButtonRef = useRef(null);
  const filtersRef = useRef(null);
  const tableRef = useRef(null);
  const noEntriesRef = useRef(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
  const [tutorialRect, setTutorialRect] = useState(null);
  const hasTutorialEntries = Boolean(data?.entries?.length);

  const tutorialSteps = useMemo(
    () => {
      const baseSteps = [
        {
          title: 'Squad Data Overview',
          body: 'This page manages your player list for Gaelic football. It is where you keep player names, teams, and positions in sync so shot entry and analysis use the correct squad options.',
          ref: pageTitleRef,
        },
        {
          title: 'Add Player',
          body: 'Use Add Player to create a new squad entry. Add players here before logging shots, otherwise the player dropdown in Add Shot will not have the right options for that team.',
          ref: addPlayerButtonRef,
        },
        {
          title: 'Filters and Search',
          body: 'Use these controls to narrow the list by position, search by player name, or change how many entries are shown per page. This helps when you are managing larger squads.',
          ref: filtersRef,
        },
      ];

      if (!hasTutorialEntries) {
        return [
          ...baseSteps,
          {
            title: 'No Players Yet',
            body: 'There are no squad entries yet. For a new user, this is expected until players are added. Once players exist, this area becomes the working table for edit, sort, and delete actions.',
            ref: noEntriesRef,
          },
        ];
      }

      return [
        ...baseSteps,
        {
          title: 'Squad Table',
          body: 'The table shows each player, their team, and their position. You can sort the columns, edit a player entry, or remove one if the details are wrong or outdated.',
          ref: tableRef,
        },
      ];
    },
    [hasTutorialEntries],
  );

  const updateTutorialRect = useCallback(() => {
    if (!showTutorial) {
      setTutorialRect(null);
      return;
    }
    const step = tutorialSteps[tutorialStepIndex];
    const node = step?.ref?.current;
    if (!node) {
      setTutorialRect(null);
      return;
    }
    const rect = node.getBoundingClientRect();
    setTutorialRect({
      top: Math.max(8, rect.top - 8),
      left: Math.max(8, rect.left - 8),
      width: rect.width + 16,
      height: rect.height + 16,
    });
  }, [showTutorial, tutorialStepIndex, tutorialSteps]);

  useEffect(() => {
    if (!showTutorial) return undefined;
    if (tutorialStepIndex > tutorialSteps.length - 1) {
      setTutorialStepIndex(Math.max(0, tutorialSteps.length - 1));
      return undefined;
    }
    const step = tutorialSteps[tutorialStepIndex];
    step?.ref?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    const refresh = () => updateTutorialRect();
    refresh();
    const timeoutId = window.setTimeout(refresh, 220);
    const secondTimeoutId = window.setTimeout(refresh, 420);
    const intervalId = window.setInterval(refresh, 70);
    const stopIntervalId = window.setTimeout(() => {
      window.clearInterval(intervalId);
    }, 800);
    window.addEventListener('resize', refresh);
    window.addEventListener('scroll', refresh, true);
    return () => {
      window.clearTimeout(timeoutId);
      window.clearTimeout(secondTimeoutId);
      window.clearTimeout(stopIntervalId);
      window.clearInterval(intervalId);
      window.removeEventListener('resize', refresh);
      window.removeEventListener('scroll', refresh, true);
    };
  }, [showTutorial, tutorialStepIndex, tutorialSteps, updateTutorialRect]);

  const startTutorial = () => {
    setTutorialStepIndex(0);
    setShowTutorial(true);
  };

  const closeTutorial = () => {
    setShowTutorial(false);
    setTutorialRect(null);
  };

  const nextTutorialStep = () => {
    if (tutorialStepIndex >= tutorialSteps.length - 1) {
      closeTutorial();
      return;
    }
    setTutorialStepIndex((prev) => prev + 1);
  };

  const prevTutorialStep = () => {
    setTutorialStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const getTutorialCardStyle = () => {
    if (!tutorialRect) return {};
    const cardWidth = Math.min(360, window.innerWidth - 32);
    const gap = 16;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const placeBelow =
      tutorialRect.top + tutorialRect.height + 220 + gap < viewportHeight;
    const top = placeBelow
      ? tutorialRect.top + tutorialRect.height + gap
      : Math.max(16, tutorialRect.top - 220 - gap);
    const preferredLeft = tutorialRect.left;
    const maxLeft = Math.max(16, viewportWidth - cardWidth - 16);
    const left = Math.min(Math.max(16, preferredLeft), maxLeft);
    return {
      position: 'fixed',
      top,
      left,
      width: cardWidth,
      zIndex: 1062,
      boxShadow: '0 16px 40px rgba(0, 0, 0, 0.35)',
      transition: 'top 160ms ease, left 160ms ease',
    };
  };

  
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleDeleteClick = (entry) => {
    setSelectedEntry(entry);
    setShowConfirm(true);
  };

  const handleConfirmDeletion = async () => {
    if (!selectedEntry) return;
    try {
      await deleteEntry(selectedEntry._id).unwrap();
      toast.success(`Entry "${selectedEntry.playerName}" deleted successfully`);
    } catch (err) {
      toast.error('Failed to delete entry. Please try again.');
    } finally {
      setShowConfirm(false);
      setSelectedEntry(null);
    }
  };

  const handleAddEntry = async (entryData) => {
    try {
      await createEntry(entryData).unwrap();
      toast.success(`Entry "${entryData.playerName}" added successfully`);
      setShowAddModal(false);
    } catch (err) {
      toast.error('Failed to add entry');
    }
  };

  const handleEditClick = (entry) => {
    setSelectedEditEntry(entry);
    setShowEditModal(true);
  };

  const handleEditEntry = async (entryData) => {
    try {
      await updateEntry(entryData).unwrap();
      toast.success(`Entry "${entryData.playerName}" updated successfully`);
      setShowEditModal(false);
      setSelectedEditEntry(null);
    } catch (err) {
      toast.error('Failed to update entry');
    }
  };

  // Sorting & filtering logic unchanged...
  let sortedData = [];
  if (data && data.entries) {
    sortedData = [...data.entries];
    if (sortConfig.key) {
      sortedData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
  }

  const uniquePositions = sortedData.reduce((acc, entry) => {
    if (!acc.includes(entry.position)) {
      acc.push(entry.position);
    }
    return acc;
  }, []);

  let filteredData = sortedData;
  if (filterPosition !== 'All') {
    filteredData = filteredData.filter(
      (entry) => entry.position.toLowerCase() === filterPosition.toLowerCase()
    );
  }
  if (searchTerm.trim() !== '') {
    filteredData = filteredData.filter((entry) =>
      entry.playerName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEntries = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <Container className="pt-4 app-section-page">
      <title>Squad Data | ShotArc</title>
      <ScreenTabs items={MANAGE_DATA_TABS} />
      <Row className="app-page-title-row align-items-center">
        <Col ref={pageTitleRef}>
          <h2 className="app-page-title">Squad Data</h2>
        </Col>
        <Col className="text-end">
          <Button
            variant="outline-light"
            size="sm"
            className="me-2 d-inline-flex align-items-center gap-2"
            onClick={startTutorial}
          >
            <FaQuestionCircle />
            Help Tour
          </Button>
          <Button
            variant="warning"
            className="text-dark fw-semibold px-3 py-2"
            onClick={() => setShowAddModal(true)}
            ref={addPlayerButtonRef}
          >
            <FaPlus /> Add Player
          </Button>
        </Col>
      </Row>

      <div className="bg-dark border border-warning rounded p-3 mb-4">
      <Row className="mb-3" ref={filtersRef}>
        <Col md={4}>
          <Form.Group controlId="filterPosition">
            <Form.Label>Filter by Position</Form.Label>
            <Form.Control
              as="select"
              value={filterPosition}
              onChange={(e) => {
                setFilterPosition(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="All">All</option>
              {uniquePositions.map((pos) => (
                <option key={pos} value={pos}>
                  {pos}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group controlId="searchTerm">
            <Form.Label>Search by Player Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group controlId="itemsPerPage">
            <Form.Label>Entries per page</Form.Label>
            <Form.Control
              as="select"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              {[10, 25, 50, 100].map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
      </Row>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error.data?.message || error.error}
        </Message>
      ) : currentEntries.length > 0 ? (
        <>
          <div className="mb-2 text-light">
            Showing {indexOfFirstItem + 1} to{' '}
            {Math.min(indexOfLastItem, totalItems)} of {totalItems} entries
          </div>

          <Table striped bordered hover responsive className="table-sm app-table" ref={tableRef}>
            <thead className="table-dark">
              <tr>
                <th
                  onClick={() => handleSort('playerName')}
                  style={{ cursor: 'pointer' }}
                >
                  Name{' '}
                  {sortConfig.key === 'playerName' &&
                    (sortConfig.direction === 'asc' ? (
                      <FaSortUp />
                    ) : (
                      <FaSortDown />
                    ))}
                </th>
                <th
                  onClick={() => handleSort('team')}
                  style={{ cursor: 'pointer' }}
                >
                  Team{' '}
                  {sortConfig.key === 'team' &&
                    (sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />)}
                </th>
                <th
                  onClick={() => handleSort('position')}
                  style={{ cursor: 'pointer' }}
                >
                  Position{' '}
                  {sortConfig.key === 'position' &&
                    (sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />)}
                </th>
                <th />
                <th />
              </tr>
            </thead>
            <tbody>
              {currentEntries.map((entry) => (
                <tr key={entry._id}>
                  <td>{entry.playerName}</td>
                  <td>{entry.team}</td>
                  <td>{entry.position}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="warning"
                      className="text-dark app-table-edit-btn"
                      onClick={() => handleEditClick(entry)}
                    >
                      <FaEdit />
                    </Button>
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDeleteClick(entry)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <Pagination className="justify-content-center app-pagination">
              <Pagination.Prev
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              />
              {[...Array(totalPages).keys()].map((num) => (
                <Pagination.Item
                  key={num + 1}
                  active={num + 1 === currentPage}
                  onClick={() => paginate(num + 1)}
                >
                  {num + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          )}
        </>
      ) : (
        <Alert className="app-empty-state text-center mb-0" ref={noEntriesRef}>
          No entries found.
        </Alert>
      )}
      </div>

      <ConfirmDeletion
        show={showConfirm}
        onConfirm={handleConfirmDeletion}
        onHide={() => setShowConfirm(false)}
        message={`Are you sure you want to delete ${
          selectedEntry?.playerName || 'this entry'
        }?`}
      />

      <AddSquadDataModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onAddPlayer={handleAddEntry}
      />

      <EditSquadDataModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        onEditPlayer={handleEditEntry}
        initialData={selectedEditEntry}
      />
      {showTutorial && (
        <>
          {tutorialRect ? (
            <>
              <div
                onClick={closeTutorial}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: tutorialRect.top,
                  background: 'rgba(8, 12, 18, 0.72)',
                  zIndex: 1060,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: 'fixed',
                  top: tutorialRect.top,
                  left: 0,
                  width: tutorialRect.left,
                  height: tutorialRect.height,
                  background: 'rgba(8, 12, 18, 0.72)',
                  zIndex: 1060,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: 'fixed',
                  top: tutorialRect.top,
                  left: tutorialRect.left + tutorialRect.width,
                  width: `calc(100vw - ${tutorialRect.left + tutorialRect.width}px)`,
                  height: tutorialRect.height,
                  background: 'rgba(8, 12, 18, 0.72)',
                  zIndex: 1060,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: 'fixed',
                  top: tutorialRect.top + tutorialRect.height,
                  left: 0,
                  width: '100vw',
                  height: `calc(100vh - ${tutorialRect.top + tutorialRect.height}px)`,
                  background: 'rgba(8, 12, 18, 0.72)',
                  zIndex: 1060,
                }}
              />
              <div
                style={{
                  position: 'fixed',
                  top: tutorialRect.top,
                  left: tutorialRect.left,
                  width: tutorialRect.width,
                  height: tutorialRect.height,
                  border: '2px solid #f59e0b',
                  borderRadius: 12,
                  pointerEvents: 'none',
                  zIndex: 1061,
                  transition:
                    'top 160ms ease, left 160ms ease, width 160ms ease, height 160ms ease',
                }}
              />
              <div className="card border-warning" style={getTutorialCardStyle()}>
                <div className="card-header bg-warning text-dark d-flex justify-content-between align-items-center">
                  <strong>
                    {tutorialStepIndex + 1} / {tutorialSteps.length}{' '}
                    {tutorialSteps[tutorialStepIndex].title}
                  </strong>
                  <Button
                    variant="link"
                    className="text-dark p-0 text-decoration-none fw-semibold"
                    onClick={closeTutorial}
                  >
                    Close
                  </Button>
                </div>
                <div className="card-body bg-dark text-white">
                  <p className="mb-3">{tutorialSteps[tutorialStepIndex].body}</p>
                  <div className="d-flex justify-content-between gap-2">
                    <Button
                      variant="outline-light"
                      size="sm"
                      disabled={tutorialStepIndex === 0}
                      onClick={prevTutorialStep}
                    >
                      Back
                    </Button>
                    <Button
                      variant="warning"
                      size="sm"
                      className="text-dark fw-semibold"
                      onClick={nextTutorialStep}
                    >
                      {tutorialStepIndex === tutorialSteps.length - 1 ? 'Finish' : 'Next'}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div
              onClick={closeTutorial}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(8, 12, 18, 0.72)',
                zIndex: 1060,
              }}
            />
          )}
        </>
      )}
    </Container>
  );
};

export default SquadDataScreen;
